import {
  endTracking,
  getPreset,
  getSourceId,
  RegistrationFlow,
  Scenario,
  startTracking,
} from '@account/account-portal/config'
import { getAccountType, registerLightUser, Touchpoint } from '@account/account-portal/lib'
import { AccountType, LightRegistrationData } from '@account/account-portal/types'
import { Consents } from '@account/consents'
import { showErrors as showConsentErrors } from '@account/consents-shared-lib'
import { GlButton } from '@adl/component-button'
import { GlForm } from '@adl/component-form'
import { GlInput } from '@adl/component-input'
import { useConfig } from '@crystal/config'
import { useDevice, useMfProps } from '@crystal/core'
import { useFeatureFlags } from '@crystal/feature-flags'
import { Translations, useTranslations } from '@crystal/translator'
import { FC, useEffect, useRef, useState } from 'react'
import '@account/consents/style.css'

import { AccountPortalConfig } from '../../config'
import { ScreenViews, useConsents, useViewData, useHydration } from '../../lib'
import {
  isMarketingConsentAccepted,
  isPaidMediaConsentAccepted,
  trackAccountType,
  trackActivationStart,
  trackLightAccountCreation,
} from '../../lib/analytics'
import { triggerActivationEmail } from '../../lib/api-client'
import { AgeConfirmationCheckBox } from '../AgeConfirmationCheckBox'
import { ErrorView } from '../ErrorView'
import { PersistentLoginCheckbox } from '../PersistentLoginCheckbox'

import { useFormData } from './account-form'
import styles from './styles.module.scss'

export const TwoStepLandingForm: FC<{ isEmailButton?: boolean; onEmailButtonClick?: () => void }> = ({
  isEmailButton,
  onEmailButtonClick,
}) => {
  const t = useTranslations<Translations>()
  const { LIGHT_ACCOUNT_DISABLED } = useFeatureFlags()
  const { emailPolicy, legalPolicy } = useConfig<AccountPortalConfig>()
  const [isPending, setIsPending] = useState(false)
  const { registerField, handleSubmit, setError, setValue, getValues } = useFormData<LightRegistrationData>()
  const { isMobile } = useDevice()
  const scenarioType = 'registration'
  const consentsState = useConsents(scenarioType)
  const mfProps = useMfProps()
  const { viewData, updateViewData, showScreen } = useViewData()
  const { isHydrated } = useHydration()
  const { isAuthProvider } = getPreset(viewData.preset)
  const isCheckout = viewData.preset === 'checkout-inline'
  const { showPersistentLoginCheckbox: showPersistentLogin } = viewData
  const showPersistentLoginCheckbox = showPersistentLogin && !isCheckout
  const touchpoint = viewData.touchpoint || Touchpoint.Unknown
  const registrationSourceId = getSourceId(touchpoint, Scenario.Registration)
  const activationSourceId = getSourceId(touchpoint, Scenario.Activation)
  const prefillData = viewData.pageContext?.prefillData
  const [apiError, setApiError] = useState<boolean>()

  const handleAccount = (isValid: boolean, screenId?: ScreenViews) => {
    if (isValid && screenId) {
      showScreen(screenId)
    } else {
      setApiError(true)
    }
  }

  async function onSubmit({
    email,
    persistentLogin = !!isAuthProvider,
    minAgeConfirmation,
    ...fakeFields
  }: LightRegistrationData) {
    startTracking('email_to_password_transition')
    setIsPending(true)
    updateViewData({
      email,
      persistentLogin,
      minAgeConfirmation,
    })

    const accountResponse = await getAccountType(email).catch(() => null)
    const accountType = accountResponse?.accountType
    const checkPaidMediaConsentAccepted = isPaidMediaConsentAccepted(consentsState?.payload)
    const checkMarketingConsentAccepted = isMarketingConsentAccepted(consentsState?.payload)
    trackAccountType(accountType)

    if (accountType === AccountType.NotRegistered) {
      // Only throw Consents error for NotRegistered users
      if (!consentsState?.isValid) {
        showConsentErrors(scenarioType)
        setIsPending(false)
        return
      }

      if (legalPolicy.minAgeRequired && !minAgeConfirmation) {
        setError('minAgeConfirmation', {
          message: t('forms.date.tooyoung'),
        })
        setIsPending(false)
        return
      }

      const time = Date.now().toString()
      const lightAccountRequest: LightRegistrationData = {
        ...fakeFields,
        email,
        minAgeConfirmation: legalPolicy.minAgeRequired ? minAgeConfirmation : true,
        sourceId: registrationSourceId,
        persistentLogin,
        legalAcceptance: consentsState?.payload?.legalAcceptance,
        createMember: true,
        formId: btoa(time),
      }

      try {
        const { registrationFlow } = getPreset(viewData.preset)
        if (LIGHT_ACCOUNT_DISABLED || registrationFlow === RegistrationFlow.FullAccount) {
          setIsPending(false)
          return handleAccount(true, ScreenViews.CREATE_PASSWORD_SCREEN)
        }

        const { acid, access_token, refresh_token } = await registerLightUser(lightAccountRequest)
        updateViewData({
          accessToken: access_token,
          refreshToken: refresh_token,
        })
        trackLightAccountCreation(acid, email, checkPaidMediaConsentAccepted, checkMarketingConsentAccepted)
        handleAccount(!!acid, ScreenViews.CREATE_PASSWORD_SCREEN)
      } catch {
        setApiError(true)
        endTracking('email_to_password_transition')
      }
    } else if (accountType === AccountType.Light) {
      try {
        const { message, acid } = await triggerActivationEmail({
          email,
          minAgeConfirmation: true,
          sendActivationEmail: true,
          sourceId: activationSourceId,
        })
        trackActivationStart(acid)
        handleAccount(message === 'success', ScreenViews.ACTIVATE_CONFIRMATION_SCREEN)
      } catch {
        setApiError(true)
        endTracking('email_to_password_transition')
      }
    } else if (accountType === AccountType.Full) {
      updateViewData({
        showPersistentLoginCheckbox: false,
      })
      handleAccount(true, ScreenViews.LOGIN_SCREEN)
    } else {
      handleAccount(false)
    }
    setIsPending(false)
  }

  // Retrieve and hold SSR value if user writes on input
  const emailRef = useRef<HTMLInputElement | null>(null)
  useEffect(() => {
    startTracking('overlay_loaded')
    const domValue = emailRef.current?.value
    if (domValue) {
      setValue('email', domValue)
    }
  }, [])

  return (
    <GlForm noValidate onSubmit={handleSubmit(onSubmit)}>
      {!isEmailButton ? (
        <GlInput
          ref={emailRef}
          id="email"
          type="email"
          autoComplete="email"
          className="gl-form-item gl-vspace-bpall-small"
          labelText={t('email.label')}
          aria-label={t('email.label')}
          readOnly={prefillData?.email && !prefillData?.email?.editable}
          {...registerField('email', {
            ...emailPolicy,
            errorMessage: t('email.error'),
            value: getValues('email'),
          })}
        />
      ) : (
        <GlButton
          id="email-login-button"
          fullWidth={isMobile}
          icon="profile"
          className={styles.email_button}
          variant="secondary"
          onClick={onEmailButtonClick}>
          {t('login.continue_with', 'e-mail')}
        </GlButton>
      )}
      {/* start of fake fields for honeypot trap */}
      <GlInput
        id="firstName"
        className={`gl-form-item gl-vspace-bpall-small ${styles.hpField}`}
        labelText="firstName"
        aria-label="firstName"
        {...registerField('firstName')}
      />
      <GlInput
        id="lastName"
        className={`gl-form-item gl-vspace-bpall-small ${styles.hpField}`}
        labelText="lastName"
        aria-label="lastName"
        {...registerField('lastName')}
      />
      <GlInput
        id="phoneNumber"
        className={`gl-form-item gl-vspace-bpall-small ${styles.hpField}`}
        labelText="phoneNumber"
        aria-label="phoneNumber"
        {...registerField('phoneNumber')}
      />
      <GlInput
        id="genderInput"
        className={`gl-form-item gl-vspace-bpall-small ${styles.hpField}`}
        labelText="genderInput"
        aria-label="genderInput"
        {...registerField('genderInput')}
      />
      {/* end of fake fields for honeypot trap */}
      {legalPolicy.minAgeRequired && (
        <AgeConfirmationCheckBox
          scenarioType={scenarioType}
          {...registerField('minAgeConfirmation', {
            type: 'checkbox',
            propsMapper: 'GlCheckbox.v5',
          })}
          required
        />
      )}
      <Consents {...mfProps} explicit scenarioType={scenarioType} />
      {showPersistentLoginCheckbox && (
        <PersistentLoginCheckbox
          id="persistentLogin"
          className="gl-form-item gl-vspace-bpall-small"
          aria-label={t('persistent.login.label')}
          {...registerField('persistentLogin')}
        />
      )}
      <ErrorView error={apiError} />
      {!isEmailButton && (
        <GlButton
          fullWidth={isMobile}
          type="submit"
          className="gl-vspace-bpall-small"
          aria-label={t('landing.button.label')}
          disabled={isPending || !isHydrated}
          id="two-step-form-button">
          {t('landing.button.label')}
        </GlButton>
      )}
      <Consents {...mfProps} implicit scenarioType={scenarioType} />
    </GlForm>
  )
}
